/**
 * Message rich-text tokenizer (shared by dashboard + widget).
 *
 * Framework-agnostic on purpose: it returns a flat list of tokens, and each app renders them as
 * React nodes (never via innerHTML), so message bodies can NEVER inject HTML — the renderer emits
 * text + a fixed set of elements, and React escapes all text. Supported inline syntax:
 *   - auto-linked `http(s)://…` URLs
 *   - `**bold**`, `*italic*`, `` `code` ``
 *   - line breaks (`\n`)
 *   - `@Agent Name` mentions — opt-in via `{ mentions: true }` (dashboard staff notes only;
 *     the widget never enables it, so visitor-facing rendering is unchanged)
 * Anything else is plain text. Only http/https links are produced, so there is no `javascript:`
 * URL vector.
 */

export type MessageToken =
  | { kind: 'text'; value: string }
  | { kind: 'link'; value: string; href: string }
  | { kind: 'bold'; value: string }
  | { kind: 'italic'; value: string }
  | { kind: 'code'; value: string }
  /** An `@`-mention; `value` is the agent name WITHOUT the leading `@` (renderers add it back). */
  | { kind: 'mention'; value: string }
  | { kind: 'br' };

// One combined scanner. Order matters: URLs and code first, then `**bold**` before `*italic*`.
const INLINE = /(https?:\/\/[^\s<>]+)|`([^`]+)`|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*/g;

// Same scanner + a 5th alternative for `@`-mentions (only used when `opts.mentions` is on).
// A mention is `@` at start-of-line/after-whitespace followed by an agent-name run: a first
// word, then optional further Capitalized words — matches names inserted by the picker
// (e.g. "@Jane Doe says hi" tokenizes the mention as "Jane Doe", not the whole sentence).
// The boundary is a CAPTURED `(^|\s)` (re-emitted as text by the tokenizer), NOT a lookbehind —
// lookbehind regex literals throw at module load on Safari/iOS < 16.4, which would crash both
// apps since this module is shared.
const INLINE_WITH_MENTIONS =
  /(https?:\/\/[^\s<>]+)|`([^`]+)`|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|(^|\s)@([A-Za-z0-9][\w.-]*(?: [A-Z][\w.-]*)*)/g;

// Trailing punctuation that is usually sentence punctuation, not part of a URL.
const URL_TRAILING = /[)\].,!?;:]+$/;

function tokenizeLine(line: string, out: MessageToken[], scanner: RegExp): void {
  let last = 0;
  scanner.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = scanner.exec(line)) !== null) {
    if (m.index > last) out.push({ kind: 'text', value: line.slice(last, m.index) });
    if (m[1] !== undefined) {
      let url = m[1];
      let trail = '';
      const tm = URL_TRAILING.exec(url);
      if (tm) {
        trail = url.slice(tm.index);
        url = url.slice(0, tm.index);
      }
      if (url) out.push({ kind: 'link', value: url, href: url });
      if (trail) out.push({ kind: 'text', value: trail });
    } else if (m[2] !== undefined) {
      out.push({ kind: 'code', value: m[2] });
    } else if (m[3] !== undefined) {
      out.push({ kind: 'bold', value: m[3] });
    } else if (m[4] !== undefined) {
      out.push({ kind: 'italic', value: m[4] });
    } else if (m[6] !== undefined) {
      // The captured boundary (start-of-line '' or one whitespace char) stays literal text.
      if (m[5]) out.push({ kind: 'text', value: m[5] });
      out.push({ kind: 'mention', value: m[6] });
    }
    last = m.index + m[0].length;
  }
  if (last < line.length) out.push({ kind: 'text', value: line.slice(last) });
}

/** Options for {@link tokenizeMessage}. */
export interface TokenizeOptions {
  /** Also emit `mention` tokens for `@Agent Name` runs. Default off (widget-safe). */
  mentions?: boolean;
}

/** Tokenize a message body into a flat, render-ready token list. */
export function tokenizeMessage(
  text: string | null | undefined,
  opts?: TokenizeOptions,
): MessageToken[] {
  const scanner = opts?.mentions ? INLINE_WITH_MENTIONS : INLINE;
  const out: MessageToken[] = [];
  const lines = (text ?? '').split('\n');
  lines.forEach((line, i) => {
    if (i > 0) out.push({ kind: 'br' });
    tokenizeLine(line, out, scanner);
  });
  return out;
}
